import { Button, Modal } from '@components/common';
import { FormInput } from '@components/common/FormField';
import { SalesClient, UpdateSalesClientInput } from '@/renderer/types/order';
import React, { useState } from 'react';

interface ClientEditorFormState {
  first_name: string;
  last_name: string;
  email: string;
  contact: string;
  address: string;
  tin_number: string;
  menu_percentage_rate: string;
}

interface ClientEditorModalProps {
  client: SalesClient;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (form: UpdateSalesClientInput) => void;
}

const toFormState = (client: SalesClient): ClientEditorFormState => ({
  first_name: client.first_name,
  last_name: client.last_name,
  email: client.email || '',
  contact: client.contact || '',
  address: client.address || '',
  tin_number: client.tin_number || '',
  menu_percentage_rate: String(client.menu_percentage_rate ?? 1),
});

export const ClientEditorModal: React.FC<ClientEditorModalProps> = ({
  client,
  isSaving,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<ClientEditorFormState>(toFormState(client));

  const set = <K extends keyof ClientEditorFormState>(
    key: K,
    value: ClientEditorFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit({
      id: client.id,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim() || null,
      contact: form.contact.trim() || null,
      address: form.address.trim() || null,
      tin_number: form.tin_number.trim() || null,
      menu_percentage_rate:
        Number(form.menu_percentage_rate) > 0
          ? Number(form.menu_percentage_rate)
          : undefined,
    });
  };

  return (
    <Modal isOpen onClose={onClose} title="Edit Client" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="First name"
            required
            value={form.first_name}
            onChange={(event) => set('first_name', event.target.value)}
          />
          <FormInput
            label="Last name"
            required
            value={form.last_name}
            onChange={(event) => set('last_name', event.target.value)}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Email"
            type="email"
            value={form.email}
            onChange={(event) => set('email', event.target.value)}
            placeholder="e.g. client@example.com"
          />
          <FormInput
            label="Contact"
            value={form.contact}
            onChange={(event) => set('contact', event.target.value)}
            placeholder="e.g. +256700000000"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Address"
            value={form.address}
            onChange={(event) => set('address', event.target.value)}
          />
          <FormInput
            label="TIN"
            value={form.tin_number}
            onChange={(event) => set('tin_number', event.target.value)}
          />
        </div>

        <FormInput
          label="Menu percentage rate"
          type="number"
          min="0"
          step="0.01"
          value={form.menu_percentage_rate}
          onChange={(event) => set('menu_percentage_rate', event.target.value)}
          fieldClassName="max-w-xs"
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};
